import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  SimpleGrid,
  Text,
  Textarea,
} from '@chakra-ui/react';
import {
  CUSTOMER_STATUS_OPTIONS,
  getCustomerCockpitProfileApi,
  upsertCustomerCockpitProfileApi,
} from 'api/customerCockpitProfileApi';
import Card from 'components/card/Card';
import SelectInput from 'components/inputs/SelectInput';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import validator from 'validator';
import { resolveGoogleDriveHref } from 'utils/driveFolderUrl';

const validateAllowedEmails = (value) => {
  if (!value || !value.trim()) return null;
  const invalidEmail = value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean)
    .find((email) => !validator.isEmail(email));
  return invalidEmail ? `Invalid email: ${invalidEmail}` : null;
};

const cockpitFields = [
  {
    label: 'Google Drive',
    name: 'googleDrivePath',
    element: Input,
  },
  {
    label: 'Slack Channel',
    name: 'slackChannelId',
    element: Input,
    placeholder: 'Channel ID (e.g. C05N9DT9G4Q)',
  },
  {
    label: 'Google Alert feed URLs',
    name: 'googleAlertFeedsText',
    element: Textarea,
    placeholder: 'One https://www.google.com/alerts/feeds/... URL per line',
  },
  {
    label: 'Google Alert context',
    name: 'googleAlertContext',
    element: Input,
    placeholder: 'Disambiguation hint for AI (optional)',
  },
  {
    label: 'Status',
    name: 'status',
    element: SelectInput,
    placeholder: 'Select status (optional)',
    options: CUSTOMER_STATUS_OPTIONS,
  },
  {
    label: 'Gross',
    name: 'gross',
    element: Input,
    placeholder: 'Please enter an explanation for the term',
  },
  {
    label: 'Net',
    name: 'net',
    element: Input,
    placeholder: 'Please enter an explanation for the term',
  },
  {
    label: 'Allowed Emails',
    name: 'allowedEmails',
    element: Input,
    placeholder: 'email1@example.com, email2@example.com (optional)',
  },
];

function profileToDraft(data) {
  if (!data) {
    return {};
  }
  const feeds = Array.isArray(data.googleAlertFeeds) ? data.googleAlertFeeds : [];
  return {
    ...data,
    googleAlertFeedsText: feeds.join('\n'),
  };
}

function EditCockpitProfile({ data, onSave }) {
  const [draft, setDraft] = useState(() => profileToDraft(data));
  const [saving, setSaving] = useState(false);
  const [allowedEmailsError, setAllowedEmailsError] = useState(null);

  useEffect(() => {
    setDraft(profileToDraft(data));
  }, [data]);

  const onFieldChange = (name, value) => {
    setDraft((prev) => ({ ...prev, [name]: value }));
    if (name === 'allowedEmails') {
      setAllowedEmailsError(validateAllowedEmails(value));
    }
  };

  const handleSave = async () => {
    const emailError = validateAllowedEmails(draft.allowedEmails);
    setAllowedEmailsError(emailError);
    if (emailError) return;

    setSaving(true);
    try {
      const feedLines = (draft.googleAlertFeedsText || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      await onSave({
        googleDrivePath: draft.googleDrivePath || '',
        slackChannelId: draft.slackChannelId || '',
        googleAlertFeeds: feedLines,
        googleAlertContext: draft.googleAlertContext || '',
        status: draft.status || '',
        gross: draft.gross || '',
        net: draft.net || '',
        allowedEmails: draft.allowedEmails || '',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <SimpleGrid columns={1} spacing={4} mt={4}>
        {cockpitFields.map((field) => {
          const Element = field.element;
          return (
            <Flex key={field.name} align="center" gap={4}>
              <Text fontSize="sm" minW="120px" flexShrink={0}>
                {field.label}
              </Text>
              <Box flex={1}>
                <Element
                  value={draft[field.name] ?? ''}
                  onChange={(e) =>
                    onFieldChange(
                      field.name,
                      e?.target ? e.target.value : e
                    )
                  }
                  placeholder={field.placeholder}
                  options={field.options}
                  rows={field.name === 'googleAlertFeedsText' ? 4 : undefined}
                />
                {field.name === 'allowedEmails' && allowedEmailsError && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    {allowedEmailsError}
                  </Text>
                )}
              </Box>
            </Flex>
          );
        })}
      </SimpleGrid>
      <Flex justify="flex-end" mt={4}>
        <Button variant="brand" onClick={handleSave} isLoading={saving}>
          Save profile
        </Button>
      </Flex>
    </Box>
  );
}

EditCockpitProfile.propTypes = {
  data: PropTypes.object,
  onSave: PropTypes.func.isRequired,
};

function CockpitProfileSection({ data }) {
  return cockpitFields.map((field) => {
    let valueNode = data?.[field.name] || '—';
    if (field.name === 'googleDrivePath') {
      const href = resolveGoogleDriveHref(data?.googleDrivePath);
      valueNode = href ? (
        <Link href={href} isExternal color="brand.500">
          link
        </Link>
      ) : (
        '—'
      );
    } else if (field.name === 'slackChannelId') {
      const slackHref = data?.slackChannelId
        ? `https://twelve-74q1359.slack.com/archives/${data.slackChannelId}`
        : null;
      valueNode = slackHref ? (
        <Link href={slackHref} isExternal color="brand.500">
          link
        </Link>
      ) : (
        '—'
      );
    } else if (field.name === 'status' && data?.status) {
      valueNode =
        CUSTOMER_STATUS_OPTIONS.find((opt) => opt.value === data.status)
          ?.label || data.status;
    } else if (field.name === 'googleAlertFeedsText') {
      const feeds = Array.isArray(data?.googleAlertFeeds) ? data.googleAlertFeeds : [];
      valueNode = feeds.length ? feeds.join('\n') : '—';
    }
    return (
      <Flex key={field.name} align="center" gap={10} py={1}>
        <Text color="secondaryGray.600" fontWeight={500} flexShrink={0}>
          {field.label}
        </Text>
        <Text>{valueNode}</Text>
      </Flex>
    );
  });
}

function CustomerCockpitProfileCard({ customerId, toast }) {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await getCustomerCockpitProfileApi(customerId);
      setProfile(data);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load cockpit profile',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [customerId]);

  const onSave = async (payload) => {
    try {
      const saved = await upsertCustomerCockpitProfileApi(customerId, payload);
      setProfile(saved);
      setEditing(false);
      toast({ title: 'Profile saved', status: 'success' });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save cockpit profile',
        status: 'error',
      });
      throw new Error('save failed');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <Fragment>
      <Card mt={6}>
        <Flex justify="space-between" align="center">
          <Heading variant={'sectionHeader'} borderColor={'secondaryGray.600'}>
            Additional settings
          </Heading>
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Cancel' : 'Edit'}
          </Button>
        </Flex>
        {editing ? (
          <EditCockpitProfile data={profile} onSave={onSave} />
        ) : (
          <Box fontSize="sm" mt={45}>
            <CockpitProfileSection data={profile} />
          </Box>
        )}
      </Card>
    </Fragment>
  );
}

CustomerCockpitProfileCard.propTypes = {
  customerId: PropTypes.string.isRequired,
  toast: PropTypes.func.isRequired,
};

export default CustomerCockpitProfileCard;
